// -- Filter and sort dealership suggestions; approximate distances (data is scraped and may be out of date)
// -- Uses substring match first, then Levenshtein ≤2 (fastest-levenshtein); physical distance used for ranking.

import { distance as levenshteinDistance } from 'fastest-levenshtein'
import type { DealershipSuggestion } from '@/data/dealershipSuggestions'

/** -- Approximate distance in km between two points (Haversine). */
export function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const MAX_SUGGESTIONS = 12
const MAX_LEVENSHTEIN = 2

export interface DealershipWithDistance extends DealershipSuggestion {
  distanceKm?: number
}

function addPhysicalDistance(
  d: DealershipSuggestion,
  userLat?: number,
  userLng?: number
): DealershipWithDistance {
  if (d.lat != null && d.lng != null && userLat != null && userLng != null) {
    return {
      ...d,
      distanceKm: haversineKm(userLat, userLng, d.lat, d.lng),
    }
  }
  return { ...d }
}

function sortByPhysicalDistance(list: DealershipWithDistance[]): void {
  list.sort((a, b) => {
    const aKm = a.distanceKm ?? Infinity
    const bKm = b.distanceKm ?? Infinity
    return aKm - bKm
  })
}

/** -- Filter by query: substring match first (ranked by physical distance), then fuzzy (Levenshtein ≤2) as lower priority. */
export function filterAndSortDealerships(
  list: readonly DealershipSuggestion[],
  query: string,
  userLat?: number,
  userLng?: number
): DealershipWithDistance[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  // -- Tier 1: substring matches (name includes query)
  const substringMatches = list.filter((d) => d.name.toLowerCase().includes(q))
  const tier1: DealershipWithDistance[] = substringMatches.map((d) =>
    addPhysicalDistance(d, userLat, userLng)
  )
  sortByPhysicalDistance(tier1)

  // -- Tier 2 & 3: fuzzy matches (Levenshtein ≤ MAX_LEVENSHTEIN), only if not already in tier1
  const tier1Names = new Set(tier1.map((d) => d.name))
  const fuzzyCandidates = list.filter((d) => {
    if (tier1Names.has(d.name)) return false
    const nameLower = d.name.toLowerCase()
    if (Math.abs(nameLower.length - q.length) > MAX_LEVENSHTEIN) return false
    return true
  })

  const tier2: DealershipWithDistance[] = []
  const tier3: DealershipWithDistance[] = []
  for (const d of fuzzyCandidates) {
    const lev = levenshteinDistance(q, d.name.toLowerCase())
    if (lev <= 0) continue
    if (lev === 1) tier2.push(addPhysicalDistance(d, userLat, userLng))
    else if (lev === 2) tier3.push(addPhysicalDistance(d, userLat, userLng))
  }
  sortByPhysicalDistance(tier2)
  sortByPhysicalDistance(tier3)

  const combined = [...tier1, ...tier2, ...tier3]
  return combined.slice(0, MAX_SUGGESTIONS)
}
